import { onCLS, onINP, onFCP, onLCP, onTTFB, Metric } from 'web-vitals';
import { captureMessage, setContext } from './monitoring';

/**
 * Web Vitals thresholds (in milliseconds)
 */
export const THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  INP: { good: 200, needsImprovement: 500 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
} as const;

/**
 * Get rating for a metric
 */
const getRating = (metric: Metric): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = THRESHOLDS[metric.name as keyof typeof THRESHOLDS];
  if (!threshold) return 'good';
  
  if (metric.value <= threshold.good) return 'good';
  if (metric.value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
};

/**
 * Report metric to monitoring service
 */
const reportMetric = (metric: Metric) => {
  const rating = getRating(metric);
  
  // Set context for debugging
  setContext('web-vitals', {
    metric: metric.name,
    value: metric.value,
    rating,
    id: metric.id,
    navigationType: metric.navigationType,
  });
  
  // Only report poor metrics to reduce noise
  if (rating === 'poor') {
    captureMessage(
      `Poor ${metric.name}: ${metric.value}ms`,
      'warning'
    );
  }
  
  // Log to console in development
  if (import.meta.env.DEV) {
    console.debug(`[Performance] ${metric.name}:`, {
      value: metric.value,
      rating,
      id: metric.id,
    });
  }
};

/**
 * Initialize Web Vitals monitoring
 */
export const initPerformanceMonitoring = () => {
  onCLS(reportMetric);
  onINP(reportMetric);
  onFCP(reportMetric);
  onLCP(reportMetric);
  onTTFB(reportMetric);
};

/**
 * Measure custom timing
 */
export const measureTiming = (name: string, startTime: number) => {
  const duration = performance.now() - startTime;
  
  if (import.meta.env.DEV) {
    console.debug(`[Timing] ${name}: ${duration.toFixed(2)}ms`);
  }
  
  // Report slow operations
  if (duration > 1000) {
    captureMessage(`Slow operation: ${name} (${duration.toFixed(0)}ms)`, 'warning');
  }
  
  return duration;
};

/**
 * Create a performance mark
 */
export const mark = (name: string) => {
  performance.mark(name);
};

/**
 * Measure between two marks
 */
export const measure = (name: string, startMark: string, endMark?: string) => {
  try {
    performance.measure(name, startMark, endMark);
    const measure = performance.getEntriesByName(name)[0];
    
    if (import.meta.env.DEV) {
      console.debug(`[Measure] ${name}: ${measure.duration.toFixed(2)}ms`);
    }
    
    return measure.duration;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Performance measurement failed:', error);
    }
    return 0;
  }
};

/**
 * Monitor component render time
 */
export const monitorRender = (componentName: string) => {
  const startTime = performance.now();
  
  return () => {
    const duration = performance.now() - startTime;
    
    if (duration > 100 && import.meta.env.DEV) {
      console.debug(`[Slow Render] ${componentName}: ${duration.toFixed(2)}ms`);
    }
  };
};

/**
 * Get current navigation timing
 */
export const getNavigationTiming = () => {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  
  if (!navigation) return null;
  
  return {
    dns: navigation.domainLookupEnd - navigation.domainLookupStart,
    tcp: navigation.connectEnd - navigation.connectStart,
    request: navigation.responseStart - navigation.requestStart,
    response: navigation.responseEnd - navigation.responseStart,
    dom: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
    load: navigation.loadEventEnd - navigation.loadEventStart,
    total: navigation.loadEventEnd - navigation.fetchStart,
  };
};
